import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Naturals Salon Private Limited",
  industry: ["Beauty Care Services", "Hair and Beauty Salon Chain"],
  sub_industry: ["Unisex Hair and Beauty Salons", "Hair Care", "Skin Care", "Body Care", "Bridal Services", "Wellness Spa Services"],
  founded_year: 2000,
  founded_original_name: "Naturals",
  headquarters: ["Chennai, Tamil Nadu, India"],
  tagline_primary: "India's No.1 Unisex Hair and Beauty Salon",
  mission:
    "Naturals' mission is to create 1000 Successful Women Entrepreneurs, open 3000 Salons & generate 50,000 Job Opportunities by December 31st, 2024 (updated target: by December 31st, 2029 based on more recent sources). Making Naturals the No. 1 salon chain in the world by 31st December 2029, empowering 1,000 women entrepreneurs, establishing 3,000 salons globally, and creating 50,000 jobs in the beauty and wellness sector. The brand's core purpose centers on democratizing beauty services and empowering women through entrepreneurship, making professional grooming accessible and affordable to all segments of society.",
  vision:
    "Naturals 3.0 aims to revolutionize the beauty industry by seamlessly integrating cutting-edge technology. We will leverage AI for personalized beauty solutions, enhance salon experiences with virtual consultations, and streamline operations with advanced tech tools, setting a new standard in beauty innovation. Our vision includes empowering over 1000 women to become successful beauty entrepreneurs. We plan to expand our presence by opening 3000 new outlets by the end of 2029. This ambitious goal will be achieved through a scalable business model, strategic location investments, and strong partnerships, ensuring widespread access to our exceptional beauty services and products. By 2029, Naturals Salon envisions creating over 50,000 job opportunities, empowering individuals and transforming communities through the beauty and wellness industry.",
  overview:
    "Naturals is India's most prominent chain of hair and beauty salons. Thanks to world-class service, courteous staff and loyal support from patrons, Naturals now has 750+ salons across the sub-continent and aims to expand to 3000 salons by 2029. Launched in 2000, Naturals is today India's No.1 unisex hair and beauty salon. Naturals pioneered a new era in the hair and beauty care industry and were one of the first brands to introduce the concept of unisex salons in India. Founded by K. Veena, whose knowledge of the international beauty industry pioneered a new era in the hair and beauty care industry in India, Naturals has made a path-breaking change in the way the beauty industry has been perceived. In the early 2000s, she made that dream a reality by setting up her first salon in Chennai. In 2004 her husband co-founder & CEO, CK Kumaravel joined and ever since then, the Naturals team has been able to meet every challenge in this competitive industry. The company operates through a franchise model and offers comprehensive beauty services including haircuts, hair coloring, facials, waxing, threading, bridal makeup, skin care, body care, and spa treatments for both men and women. Being a versatile yet affordable salon brand that caters to a whole range of people from different walks of life, Naturals flourished over the years to become a name synonymous with hair and beauty care in India. The brand operates multiple formats including Naturals Unisex Salon (mainstream), Naturals Lounge (premium), Naturals W (women-only), and Naturals Signature Salon, along with Naturals Beauty Academy for professional training and Nails N Beyond for specialized nail care. To steer this consistent growth the brand has joined hands with the style diva and Bollywood queen, Kareena Kapoor as their new brand ambassador. The company has a strong focus on women entrepreneurship, with over 500 financially independent women entrepreneurs operating franchise salons. In 2024, Naturals Salon significantly expanded its global presence, opening doors in the Middle East and Singapore. This milestone marked the brand's international recognition and growth potential.",
  scale: {
    revenue: 5.9,
    employees_estimate: 300,
    markets_served_count: 8,
    operating_countries_count: 4,
  },
  global_presence: {
    regions: [
      {
        region: "South India",
        headquarters: "Chennai, Tamil Nadu, India",
        active_markets: ["Tamil Nadu", "Karnataka", "Andhra Pradesh", "Telangana", "Kerala"],
        market_type: "Owned and Franchise",
      },
      {
        region: "Middle East (UAE)",
        headquarters: "Dubai, UAE",
        active_markets: ["United Arab Emirates", "Dubai", "Abu Dhabi"],
        market_type: "Franchise",
      },
      {
        region: "Southeast Asia",
        headquarters: "Singapore",
        active_markets: ["Singapore"],
        market_type: "Franchise",
      },
      {
        region: "South Asia (Sri Lanka)",
        headquarters: "Not found",
        active_markets: ["Sri Lanka"],
        market_type: "Franchise",
      },
      {
        region: "West India",
        headquarters: "Not found",
        active_markets: ["Maharashtra", "Gujarat", "Goa"],
        market_type: "Franchise",
      },
      {
        region: "North India",
        headquarters: "Not found",
        active_markets: ["Delhi NCR", "Uttar Pradesh", "Madhya Pradesh"],
        market_type: "Franchise",
      },
      {
        region: "East India",
        headquarters: "Not found",
        active_markets: ["West Bengal", "Odisha"],
        market_type: "Franchise",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "Tamil Nadu, India",
        share_percent: 32.5,
        notes: "Largest market with approximately 30-35% of total salon count (estimated based on 220 of them in Tamil Nadu out of historical 390 salons)",
      },
      {
        region: "Karnataka, India",
        share_percent: 12.5,
        notes: "Approximately 10-15% of salon network (estimated based on 50 each in Andhra Pradesh and Karnataka)",
      },
      {
        region: "Andhra Pradesh & Telangana, India",
        share_percent: 12.5,
        notes: "Approximately 10-15% of salon network",
      },
      {
        region: "Maharashtra, India",
        share_percent: 0,
        notes: "Growing presence in Mumbai, Pune metro areas",
      },
      {
        region: "Kerala, India",
        share_percent: 0,
        notes: "Moderate presence",
      },
      {
        region: "United Arab Emirates (Dubai, Abu Dhabi)",
        share_percent: 0,
        notes: "International market - early stage with multiple locations",
      },
      {
        region: "Other Indian States (Gujarat, Delhi NCR, West Bengal, etc.)",
        share_percent: 0,
        notes: "Distributed across tier-2 and tier-3 cities",
      },
      {
        region: "Singapore & Sri Lanka",
        share_percent: 0,
        notes: "Emerging international markets",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Naturals Purple",
          hex: "#7B3F9E",
        },
        {
          name: "Naturals White",
          hex: "#FFFFFF",
        },
        {
          name: "Naturals Black",
          hex: "#000000",
        },
      ],
      secondary: [
        {
          name: "Soft Lavender",
          hex: "#E6D7F0",
        },
        {
          name: "Warm Gold",
          hex: "#D4AF37",
        },
        {
          name: "Neutral Gray",
          hex: "#F5F5F5",
        },
      ],
      accent: [
        {
          name: "Deep Plum",
          hex: "#5C2D6F",
        },
        {
          name: "Rose Gold",
          hex: "#B76E79",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif",
        weight: "Bold",
        usage: "Headings",
      },
      {
        font_family: "Sans-serif",
        weight: "Regular",
        usage: "Body",
      },
    ],
    logos: [
      {
        variant: "Primary Logo (Full Wordmark)",
        url: "https://www.naturals.in/assets/mobile/nat_logo_svg.svg",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Empowering", "Accessible", "Professional", "Aspirational", "Inclusive", "Confident", "Warm & Welcoming"],
    taglines: ["India's No.1 hair and beauty salon", "World's Fastest Growing Salon Chain", "800+ Salons | 25+ Years of Excellence"],
    messaging_dos: [
      "Empowering over 1000 women to become successful beauty entrepreneurs",
      "Creating 1000 successful women entrepreneurs, opening 3000 salons, and generating 50,000 job opportunities",
      "From Hair to Heels, We've Got You Covered",
      "Expert Haircuts, Beauty Treatments & Styling",
      "Transforming beauty worldwide",
    ],
    messaging_donts: [],
    banned_words: [],
  },
  creative_assets: ["https://www.naturals.in/assets/mobile/nat_logo_svg.svg"],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["Naturals Unisex Salon", "Naturals Lounge", "Naturals W", "Naturals Signature Salon", "Naturals Beauty Academy", "Nails N Beyond"],
    selected_brand: "Naturals",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "Bridal Campaign Banner",
        description: "Accent color deviates from Naturals Purple #7B3F9E",
        severity: "high",
      },
      {
        title: "Salon Launch Landing Page",
        description: "Heading font size inconsistent with typography scale",
        severity: "medium",
      },
      {
        title: "Social Media Ad - Instagram",
        description: "Logo spacing does not meet minimum clearance requirements",
        severity: "low",
      },
      {
        title: "Naturals Lounge Brochure",
        description: "Rose Gold accent usage exceeds recommended 20% threshold",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Elegant Beauty", score: 94, change: 5 },
      { preference: "Purple Accents", score: 90, change: 4 },
      { preference: "Luxury Feel", score: 86, change: 3 },
      { preference: "Female-Focused", score: 82, change: 7 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.2,
    sentiment_graph: [
      {
        metric: "Customer Satisfaction Score (CSAT)",
        value: "78%",
        delta: "+4.2%",
      },
      {
        metric: "Market Share (India Salon Industry)",
        value: "4.8%",
        delta: "+0.6%",
      },
      {
        metric: "Network Growth Rate",
        value: "52%",
        delta: "+8.5%",
      },
      {
        metric: "Brand Recognition Index",
        value: "82/100",
        delta: "+5.0",
      },
      {
        metric: "Franchise Expansion Rate",
        value: "750+ outlets",
        delta: "+12%",
      },
      {
        metric: "Revenue (FY 2024)",
        value: "₹49.1 Cr",
        delta: "+18.3%",
      },
      {
        metric: "Women Entrepreneur Empowerment",
        value: "500+",
        delta: "+15%",
      },
      {
        metric: "Share of Voice (Digital)",
        value: "24%",
        delta: "+2.8%",
      },
      {
        metric: "Sentiment Score",
        value: "72%",
        delta: "+3.8%",
      },
      {
        metric: "Net Promoter Score (Estimated)",
        value: "+42",
        delta: "+6",
      },
      {
        metric: "Market Share (Salon Industry)",
        value: "4.8%",
        delta: "+0.6%",
      },
      {
        metric: "Share of Voice (Beauty Services)",
        value: "24%",
        delta: "+2.8%",
      },
      {
        metric: "Customer Retention Rate",
        value: "68%",
        delta: "+4.2%",
      },
      {
        metric: "Service Quality Rating",
        value: "7.8/10",
        delta: "+0.5",
      },
      {
        metric: "Brand Trust Index",
        value: "81%",
        delta: "+3.5%",
      },
      {
        metric: "Franchise Satisfaction Score",
        value: "76%",
        delta: "+5.1%",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["Last 12 months (2024-2025)"],
    themes: {
      positive: ["Affordable Premium Services", "Professional Staff Expertise", "Hygiene and Cleanliness", "Wide Service Range", "Franchise Accessibility", "Pleasant Ambience", "Value for Money"],
      neutral: ["Service Consistency Variability", "Appointment Booking Issues", "Pricing Transparency", "Staff Training Levels", "Wait Time Management"],
      negative: [
        "Inconsistent Service Quality",
        "Pricing Discrepancies",
        "Treatment Durability Issues",
        "Customer Service Response",
        "Staff Skill Gaps",
        "Rushed Service Experience",
        "Product Quality Concerns",
      ],
    },
  },
  distribution: [
    {
      brand: "Jawed Habib",
      share: 18.5,
    },
    {
      brand: "Lakmé Salon",
      share: 14.2,
    },
    {
      brand: "Naturals",
      share: 12.8,
    },
    {
      brand: "VLCC",
      share: 9.5,
    },
    {
      brand: "Others",
      share: 45,
    },
  ],
  sentiment_by_product: [
    { product: "Hair Spa & Treatments", sentiment: 8.2, change_delta: 6 },
    { product: "Haircut Services", sentiment: 7.4, change_delta: 3 },
    { product: "Facial Services", sentiment: 7.9, change_delta: 4 },
    { product: "Bridal Services", sentiment: 8.5, change_delta: 8 },
    { product: "Keratin/Smoothening", sentiment: 5.8, change_delta: -8 },
    { product: "Waxing & Threading", sentiment: 7.1, change_delta: 2 },
    { product: "Body Massage & Spa", sentiment: 8.0, change_delta: 5 },
    { product: "Nail Care (Nails N Beyond)", sentiment: 7.7, change_delta: 9 },
  ],
  top_topics: {
    praise: [
      {
        topic: "Affordable Pricing",
        weight: 10,
      },
      {
        topic: "Professional Staff",
        weight: 9,
      },
      {
        topic: "Hygiene Standards",
        weight: 8,
      },
      {
        topic: "Wide Service Range",
        weight: 7,
      },
      {
        topic: "Pleasant Ambience",
        weight: 6,
      },
      {
        topic: "Accessible Network",
        weight: 5,
      },
      {
        topic: "Specific Stylist Excellence",
        weight: 4,
      },
    ],
    complaints: [
      {
        topic: "Service Inconsistency",
        weight: 10,
      },
      {
        topic: "Pricing Transparency",
        weight: 9,
      },
      {
        topic: "Treatment Durability",
        weight: 8,
      },
      {
        topic: "Staff Training Gaps",
        weight: 7,
      },
      {
        topic: "Customer Service Response",
        weight: 6,
      },
      {
        topic: "Rushed Service",
        weight: 5,
      },
      {
        topic: "Appointment Management",
        weight: 4,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "Lakmé Salon",
      threat_level: "high",
      market_share: 16.5,
      sentiment: 78,
    },
    {
      name: "Jawed Habib Hair & Beauty",
      threat_level: "high",
      market_share: 13.5,
      sentiment: 72,
    },
    {
      name: "Green Trends",
      threat_level: "high",
      market_share: 9,
      sentiment: 70,
    },
    {
      name: "Geetanjali Salon",
      threat_level: "medium-high",
      market_share: 6,
      sentiment: 75,
    },
    {
      name: "Looks Salon",
      threat_level: "medium",
      market_share: 5,
      sentiment: 68,
    },
    {
      name: "VLCC",
      threat_level: "medium",
      market_share: 4,
      sentiment: 70,
    },
    {
      name: "Strands Salons",
      threat_level: "medium",
      market_share: 3.5,
      sentiment: 65,
    },
    {
      name: "Enrich Salon",
      threat_level: "medium",
      market_share: 3,
      sentiment: 67,
    },
  ],
  secondary_competitors: [
    {
      name: "Urban Company",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Shahnaz Husain Group",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Toni & Guy",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Studio11 Salon & Spa",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Truefitt & Hill",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Jean Claude Biguine",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Digital/Social Media",
      distribution: {
        Naturals: 13.6,
        "Lakmé Salon": 26.6,
        "Jawed Habib": 16.6,
        "Green Trends": 11.1,
        VLCC: 9,
        "Urban Company": 4,
        "Shahnaz Husain": 3.5,
        Geetanjali: 2.6,
        Others: 13.0,
      },
    },
    {
      channel: "Search/SEO",
      distribution: {
        Naturals: 19.1,
        "Lakmé Salon": 23.6,
        "Jawed Habib": 16.6,
        "Green Trends": 9,
        VLCC: 4.2,
        "Urban Company": 13.6,
        "Shahnaz Husain": 3.1,
        Geetanjali: 2.8,
        Others: 7.4,
      },
    },
    {
      channel: "Traditional Media (Print/OOH)",
      distribution: {
        Naturals: 13.4,
        "Lakmé Salon": 32.3,
        "Jawed Habib": 10.9,
        "Green Trends": 4.1,
        VLCC: 16.4,
        "Urban Company": 2.8,
        "Shahnaz Husain": 9,
        Geetanjali: 29.1,
        Others: 9.0,
      },
    },
    {
      channel: "Influencer/Celebrity Endorsements",
      distribution: {
        Naturals: 16.8,
        "Lakmé Salon": 29.6,
        "Jawed Habib": 21.4,
        "Green Trends": 5.2,
        VLCC: 3.4,
        "Urban Company": 2.6,
        "Shahnaz Husain": 2.2,
        Geetanjali: 13.8,
        Others: 5.0,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness (Aided Recall)",
      brands: [
        {
          brand_name: "Lakmé Salon",
          score: 85,
        },
        {
          brand_name: "Jawed Habib",
          score: 80,
        },
        {
          brand_name: "Naturals",
          score: 70,
        },
        {
          brand_name: "VLCC",
          score: 75,
        },
        {
          brand_name: "Green Trends",
          score: 60,
        },
        {
          brand_name: "Geetanjali",
          score: 65,
        },
      ],
    },
    {
      metric_name: "Franchise Satisfaction Index",
      brands: [
        {
          brand_name: "Naturals",
          score: 72,
        },
        {
          brand_name: "Lakmé Salon",
          score: 78,
        },
        {
          brand_name: "Jawed Habib",
          score: 70,
        },
        {
          brand_name: "Green Trends",
          score: 75,
        },
        {
          brand_name: "Geetanjali",
          score: 80,
        },
        {
          brand_name: "VLCC",
          score: 68,
        },
      ],
    },
    {
      metric_name: "Service Quality Rating",
      brands: [
        {
          brand_name: "Geetanjali Salon",
          score: 82,
        },
        {
          brand_name: "Lakmé Salon",
          score: 80,
        },
        {
          brand_name: "Naturals",
          score: 75,
        },
        {
          brand_name: "Jawed Habib",
          score: 72,
        },
        {
          brand_name: "Green Trends",
          score: 70,
        },
        {
          brand_name: "Looks Salon",
          score: 68,
        },
      ],
    },
    {
      metric_name: "Digital Engagement Score",
      brands: [
        {
          brand_name: "Lakmé Salon",
          score: 85,
        },
        {
          brand_name: "Jawed Habib",
          score: 70,
        },
        {
          brand_name: "Naturals",
          score: 60,
        },
        {
          brand_name: "Green Trends",
          score: 55,
        },
        {
          brand_name: "Urban Company",
          score: 90,
        },
        {
          brand_name: "VLCC",
          score: 65,
        },
      ],
    },
    {
      metric_name: "Market Expansion Velocity",
      brands: [
        {
          brand_name: "Naturals",
          score: 78,
        },
        {
          brand_name: "Jawed Habib",
          score: 85,
        },
        {
          brand_name: "Lakmé Salon",
          score: 75,
        },
        {
          brand_name: "Green Trends",
          score: 70,
        },
        {
          brand_name: "Monsoon Salon",
          score: 82,
        },
        {
          brand_name: "YLG",
          score: 65,
        },
      ],
    },
    {
      metric_name: "Price-Value Perception",
      brands: [
        {
          brand_name: "Green Trends",
          score: 85,
        },
        {
          brand_name: "Naturals",
          score: 80,
        },
        {
          brand_name: "Jawed Habib",
          score: 75,
        },
        {
          brand_name: "Lakmé Salon",
          score: 70,
        },
        {
          brand_name: "Geetanjali",
          score: 60,
        },
        {
          brand_name: "Toni & Guy",
          score: 50,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: [
      "Women entrepreneurship focus (500+ women franchisees)",
      "Affordable luxury positioning",
      "Strong South India dominance",
      "Multiple format strategy (Lounge, W, Signature)",
      "International expansion (UAE, Singapore)",
    ],
    improvement_areas: [
      "Digital/social media presence lags Lakmé significantly (63K vs 140K Instagram)",
      "Share of voice in national media lower than top 2 competitors",
      "Brand awareness outside South India needs strengthening",
      "Technology integration behind digital-first competitors",
    ],
    disruption_watchlist: ["Urban Company", "YLG Salon & Spa", "Monsoon Salon", "Trimy Tones"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Naturals salon targets millennials and Gen Z, focusing on women aged 21-50 from upper socioeconomic classes, with a strong concentration in the 25-34 age range. The audience is value-conscious, career-oriented, and influenced by social media, seeking quality and convenience in beauty services.",
  core_demographics: {
    primary_age_range: "25-34",
    gender_split: {
      male: 27.5,
      female: 72.5,
    },
    income_median_usd: 7800,
    location_type: "Urban",
    demographics_distribution: {
      age: [
        { age_range: "18-24", percentage: 20 },
        { age_range: "25-34", percentage: 38 },
        { age_range: "35-44", percentage: 24 },
        { age_range: "45-54", percentage: 12 },
        { age_range: "55+", percentage: 6 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 44 },
        { education_level: "Some College", percentage: 30 },
        { education_level: "Graduate Degree", percentage: 14 },
        { education_level: "High School", percentage: 12 },
      ],
      geographic: [
        { location: "Urban", percentage: 62 },
        { location: "Suburban", percentage: 28 },
        { location: "Rural", percentage: 10 },
      ],
    },
  },
  occupation_distribution: [
    { occupation: "Working Professionals", percentage: 42 },
    { occupation: "Students", percentage: 18 },
    { occupation: "Homemakers", percentage: 22 },
    { occupation: "Entrepreneurs / Self-Employed", percentage: 10 },
    { occupation: "Bridal / Occasion-Based Clients", percentage: 8 },
  ],
  interests: {
    top_categories: [
      {
        category: "Fashion & Beauty Trends",
        score: 9,
      },
      {
        category: "Social Media & Digital Content",
        score: 8.5,
      },
      {
        category: "Career Development & Professional Growth",
        score: 8,
      },
      {
        category: "Health & Wellness",
        score: 7.5,
      },
      {
        category: "Bollywood & Entertainment",
        score: 7,
      },
      {
        category: "Travel & Lifestyle",
        score: 6.5,
      },
      {
        category: "Technology & Apps",
        score: 6,
      },
      {
        category: "Fitness & Yoga",
        score: 6,
      },
      {
        category: "Shopping & E-commerce",
        score: 7.5,
      },
      {
        category: "Photography & Instagram Aesthetics",
        score: 8,
      },
    ],
    brand_affinities: [
      {
        brand: "Lakmé",
        affinity: 75,
      },
      {
        brand: "Nykaa",
        affinity: 70,
      },
      {
        brand: "Fabindia",
        affinity: 65,
      },
      {
        brand: "Tanishq",
        affinity: 60,
      },
      {
        brand: "Myntra",
        affinity: 68,
      },
      {
        brand: "Zomato/Swiggy",
        affinity: 65,
      },
      {
        brand: "Himalaya Herbals",
        affinity: 62,
      },
      {
        brand: "Forest Essentials",
        affinity: 58,
      },
      {
        brand: "Titan",
        affinity: 60,
      },
      {
        brand: "Reliance Trends",
        affinity: 55,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Walk-in to Physical Salon": 65,
        "Online Booking (App/Website)": 25,
        "Phone Booking": 8,
        "Home Service": 2,
      },
      purchase_frequency: "1.5-2 times per month",
      avg_order_value_usd: 20,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Need Recognition",
          description: "Special occasion, routine maintenance, or social media inspiration triggers salon need",
          duration_days: 2,
        },
        {
          stage: 2,
          name: "Salon Discovery",
          description: "Google search 'salon near me', check Instagram, ask friends, read reviews",
          duration_days: 1,
        },
        {
          stage: 3,
          name: "Service & Price Comparison",
          description: "Compare services offered, check pricing, view before/after photos, assess hygiene standards",
          duration_days: 1,
        },
        {
          stage: 4,
          name: "Appointment Scheduling",
          description: "Book via app/phone/walk-in, select preferred time slot and stylist",
          duration_days: 1,
        },
        {
          stage: 5,
          name: "Experience Sharing",
          description: "Share results on social media, leave reviews, recommend to friends, rebook for next visit",
          duration_days: 4,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        "Mobile/Smartphone": 75,
        "Desktop/Laptop": 18,
        Tablet: 5,
        "In-person/Walk-in": 2,
      },
      media_channels: {
        Instagram: 35,
        Facebook: 25,
        YouTube: 15,
        WhatsApp: 12,
        "Google Search": 8,
        "Word of Mouth/Referrals": 3,
        "TikTok/Reels": 2,
      },
    },
  },
  platforms: {
    distribution: {
      Instagram: 35,
      Facebook: 25,
      YouTube: 15,
      WhatsApp: 12,
      "Google Search": 8,
      "Word of Mouth/Referrals": 3,
      "TikTok/Reels": 2,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "Instagram",
      followers: 63000,
      health_score: 60,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "LinkedIn",
      followers: 8136,
      health_score: 60,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Twitter/X",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Pinterest",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Google Business Profile",
      followers: 0,
      health_score: 0,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 40,
    },
    {
      label: "Neutral",
      percentage: 40,
    },
    {
      label: "Negative",
      percentage: 20,
    },
  ],
  recent_comments: [
    {
      user: "beautylover123",
      platform: "Instagram",
      timeAgo: "2 days ago",
      status: "positive",
      commentText: "Love the new hair transformation!",
      postContext: "Before & After post",
    },
    {
      user: "saloncritic",
      platform: "LinkedIn",
      timeAgo: "1 week ago",
      status: "neutral",
      commentText: "Interesting recruitment strategy.",
      postContext: "Corporate communication post",
    },
    {
      user: "fashionista",
      platform: "Instagram",
      timeAgo: "3 days ago",
      status: "negative",
      commentText: "Not impressed with the service.",
      postContext: "Customer feedback post",
    },
  ],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 2,
      },
      {
        platform: "Facebook",
        engagement_rate: 75,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 55,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Before & After Transformations",
        performance: 9,
      },
      {
        content_type: "Instagram Reels / Short-Form Video",
        performance: 8,
      },
      {
        content_type: "Hair Care Tips & Tutorials",
        performance: 7,
      },
      {
        content_type: "User-Generated Content (Customer Posts)",
        performance: 8,
      },
      {
        content_type: "Instagram Stories",
        performance: 6,
      },
      {
        content_type: "Behind-the-Scenes / Team Spotlights",
        performance: 5,
      },
      {
        content_type: "Promotional Posts / Offers",
        performance: 4,
      },
    ],
    follower_growth_trend: [
      {
        month: "2020-01",
        followers: 50000,
      },
      {
        month: "2021-01",
        followers: 60000,
      },
      {
        month: "2022-01",
        followers: 70000,
      },
      {
        month: "2023-01",
        followers: 80000,
      },
      {
        month: "2024-01",
        followers: 90000,
      },
      {
        month: "2025-01",
        followers: 100000,
      },
    ],
  },
};

export const mBrandIntelligenceNaturalsSalon: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
